import { api, ROLES, roleLabel, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2, KeyRound, UserX, UserCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useEffect, useState } from "react";

const empty = { email: "", password: "", name: "", role: "sales", phone: "" };

export default function TeamPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [resetFor, setResetFor] = useState(null); // user object being reset
  const [resetPw, setResetPw] = useState("");
  const [resetForce, setResetForce] = useState(true);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const isAdmin = user?.role === "admin";

  const submit = async () => {
    try {
      await api.post("/users", form);
      setOpen(false); load(); toast.success("Member added");
      setForm(empty);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.name}? Only allowed if they have no linked records.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      load(); toast.success("Removed");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleActive = async (u) => {
    try {
      if (u.active === false) {
        await api.post(`/users/${u.id}/activate`);
        toast.success(`${u.name} activated`);
      } else {
        if (!window.confirm(`Deactivate ${u.name}? They will not be able to log in.`)) return;
        await api.post(`/users/${u.id}/deactivate`);
        toast.success(`${u.name} deactivated`);
      }
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const submitReset = async () => {
    if (resetPw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try {
      await api.post(`/users/${resetFor.id}/reset-password`, {
        new_password: resetPw, force_change: resetForce
      });
      toast.success(`${resetFor.name}'s password reset`);
      setResetFor(null); setResetPw(""); setResetForce(true);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const formatDate = (iso) => {
    if (!iso) return "Never";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
  };

  return (
    <div className="space-y-4" data-testid="team-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">People</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Team Members</h1>
        </div>
        {isAdmin && <Button onClick={() => setOpen(true)} data-testid="new-user-btn"><Plus className="h-4 w-4 mr-2" />Add Member</Button>}
      </div>

      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Last Login</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {users.map((u) => {
              const inactive = u.active === false;
              return (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`} className={inactive ? "opacity-60" : ""}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded-full bg-primary/20 border border-primary/40 grid place-items-center text-xs font-semibold">{u.name[0]}</div>
                      {u.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    {(u.roles && u.roles.length > 0 ? u.roles : [u.role]).map((r) => (
                      <Badge key={r} variant="outline" className="capitalize mr-1">{roleLabel(r)}</Badge>
                    ))}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{u.phone || "—"}</TableCell>
                  <TableCell>
                    {inactive ? (
                      <Badge className="bg-red-500/20 text-red-300 border-red-500/30 border">Inactive</Badge>
                    ) : (
                      <div className="flex items-center gap-1 flex-wrap">
                        <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 border">Active</Badge>
                        {u.must_change_password && (
                          <span title="Must change password on next login" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300">
                            <AlertCircle className="h-3 w-3" />PW change
                          </span>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">
                    {formatDate(u.last_login_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    {isAdmin && u.id !== user.id && (
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => { setResetFor(u); setResetPw(""); setResetForce(true); }}
                                title="Reset password" data-testid={`reset-pw-${u.id}`}>
                          <KeyRound className="h-4 w-4 text-primary" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => toggleActive(u)}
                                title={inactive ? "Activate" : "Deactivate"}
                                data-testid={`toggle-active-${u.id}`}>
                          {inactive ? <UserCheck className="h-4 w-4 text-emerald-400" /> : <UserX className="h-4 w-4 text-amber-400" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => removeUser(u)} data-testid={`delete-user-${u.id}`} title="Delete">
                          <Trash2 className="h-4 w-4 text-red-400" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Add member */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <TField label="Full Name" required>
              <Input placeholder="e.g. Rohan Mehta" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </TField>
            <TField label="Email" required hint="Used to sign in">
              <Input placeholder="rohan@tappav.com" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </TField>
            <TField label="Temporary Password" required hint="Minimum 8 characters. Member will be forced to change it on first login.">
              <Input placeholder="Type a temp password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </TField>
            <TField label="Phone" hint="For direct contact">
              <Input placeholder="+91-…" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </TField>
            <TField label="Primary Role" required>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}</SelectContent>
              </Select>
            </TField>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="user-save-btn" disabled={form.password.length < 8}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetFor} onOpenChange={(v) => !v && setResetFor(null)}>
        <DialogContent className="bg-card border-border" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset password — {resetFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <TField label="New Password" required hint="Minimum 8 characters">
              <Input type="password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} data-testid="reset-pw-input" />
            </TField>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={resetForce} onChange={(e) => setResetForce(e.target.checked)} />
              Force member to change password on next login
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setResetFor(null)}>Cancel</Button>
            <Button onClick={submitReset} disabled={resetPw.length < 8} data-testid="reset-pw-submit">Reset</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TField({ label, hint, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10.5px] text-muted-foreground/70 mt-1">{hint}</div>}
    </div>
  );
}
